import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import ReactPaginate from 'react-paginate';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import { fetchAllBis } from '../../catalog/infrastructure/CatalogService';
import { initial } from '../../../common/domain/RemoteData.model';
import { remoteDataWithSetter } from '../../../common/utils/remoteDataWithSetter';
import { RemoteDataUI } from '../../../common/ui/RemoteDataUI';
import { Cardsection } from '../../../ui';
import { CatalogItemCard } from '../../../common/ui/CatalogItemCard';
import { PriceWithFallback } from '../../../common/ui/PriceWithFallback';
import { ProductFlatPurchaseCardUI } from '../../../product/ui/ProductFlatPurchaseCardUI';
import { useComercialContext } from '../../../comercial/ComercialContext';
import { useDebouncedEffect } from '../../../common/utils/useDemouncedEffect';
import { useFilterContext } from '../../catalog/infrastructure/FilterContext';
import { FilterSection } from '../../catalog/ui/FilterSection';
import {
    SearchWrapper,
    SearchContainer,
    SearchInput,
    SearchIconWrapper,
} from '../../catalog/ui/filter-panel/ProductSearchFilter.styles';

const PAGE_SIZE = 20;

// El naranja de las etiquetas "Combinable" (mismo valor que en TagListUI.jsx).
const PROMO_ORANGE = '#f0541e';

const Hero = styled.div`
    background: ${PROMO_ORANGE};
    border-radius: 16px;
    padding: 2rem 1.5rem 2.25rem;
    margin-top: 1.5rem;
    margin-bottom: 2rem;
    color: white;

    h1 {
        color: white;
        margin-bottom: 0.25rem;
    }

    p {
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 1.25rem;
    }
`;

const DivPagination = styled.div`
    padding: 1.5rem 0 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TabsBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
`;

const TabButton = styled.button`
    border: 2px solid ${PROMO_ORANGE};
    border-radius: 999px;
    padding: 0.4rem 1.25rem;
    font-weight: 600;
    background: ${({ $active }) => ($active ? PROMO_ORANGE : 'white')};
    color: ${({ $active }) => ($active ? 'white' : PROMO_ORANGE)};
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${({ $active }) => ($active ? PROMO_ORANGE : '#fdeee8')};
    }
`;

const hasActivePanelFilters = (panelFilter) =>
    !!panelFilter?.taxonomyTree?.macrofamilyId
    || (panelFilter?.manufacturerIds?.size ?? 0) > 0
    || (panelFilter?.species?.size ?? 0) > 0
    || panelFilter?.characteristics?.prescription != null;

/**
 * Una seccion de la pagina de Promociones: cabecera + listado paginado de los productos que
 * cumplen la caracteristica (combinables, corta caducidad u ofertas), filtrados ademas por el
 * buscador propio de la pagina y por el panel lateral (categorias, fabricantes, especies,
 * prescripcion). Todo el filtrado se hace en el servidor reutilizando el endpoint del catalogo.
 */
const PromoSection = ({ title, characteristics, search, panelFilter, viewMode, hideWhenEmpty }) => {
    const { actingAsCliente } = useComercialContext();
    const [remoteData, setRemoteData] = useState(initial(null));
    const [page, setPage] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [search, panelFilter]);

    useEffect(() => {
        const actingAsNumCliente = actingAsCliente?.numCliente ?? null;
        const fetchFn = () => fetchAllBis(
            {
                search: search || null,
                taxonomyTree: panelFilter?.taxonomyTree,
                manufacturerIds: panelFilter?.manufacturerIds,
                species: panelFilter?.species,
                characteristics: {
                    ...characteristics,
                    prescription: panelFilter?.characteristics?.prescription,
                },
            },
            { page, amount: PAGE_SIZE },
            actingAsNumCliente
        );
        remoteDataWithSetter(setRemoteData, fetchFn).then();
    }, [search, panelFilter, page, actingAsCliente, characteristics]);

    const filtered = !!search || hasActivePanelFilters(panelFilter);

    return (
        <RemoteDataUI remoteData={remoteData}>
            <RemoteDataUI.Pending>
                <div className="text-center m-4"><Spinner /></div>
            </RemoteDataUI.Pending>

            <RemoteDataUI.Failure>
                <div className="alert alert-danger">Imposible cargar productos. Intentelo más tarde.</div>
            </RemoteDataUI.Failure>

            <RemoteDataUI.Success>
                {catalog => {
                    // En la vista "Todas", una seccion sin productos y sin filtros se oculta
                    // entera (igual que en la portada). Con su pestana seleccionada o con
                    // filtros/busqueda activos se muestra el aviso.
                    if (catalog.content.length === 0 && !filtered && hideWhenEmpty) return null;

                    return (
                        <section className="mb-4">
                            <div className="border-start border-4 border-primary ps-3 mb-3">
                                <p className="fw-bolder text-primary fs-5 mb-0">{title}</p>
                            </div>

                            {catalog.content.length === 0 ? (
                                <p className="text-muted ms-1">
                                    {search
                                        ? <>Sin resultados para «{search}» en esta sección.</>
                                        : filtered
                                            ? <>Sin resultados con los filtros aplicados en esta sección.</>
                                            : <>Ahora mismo no hay productos en esta sección.</>}
                                </p>
                            ) : (
                                <>
                                    {viewMode === 'grid' ? (
                                        <Cardsection>
                                            {catalog.content.map((product, index) => (
                                                <div key={index}>
                                                    <CatalogItemCard data={{ catalogItem: product }}>
                                                        <CatalogItemCard.Price>
                                                            <PriceWithFallback data={{
                                                                sellingPrice: product.price?.bestPrice,
                                                                basePrice: product.price?.value
                                                            }} />
                                                        </CatalogItemCard.Price>
                                                    </CatalogItemCard>
                                                </div>
                                            ))}
                                        </Cardsection>
                                    ) : (
                                        <div className="cardList">
                                            {catalog.content.map((product, index) => (
                                                <div key={index} className="my-1">
                                                    <ProductFlatPurchaseCardUI details={product} />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {catalog.totalPages > 1 && (
                                        <DivPagination>
                                            <ReactPaginate
                                                previousLabel="<"
                                                nextLabel=">"
                                                pageClassName="page-item"
                                                pageLinkClassName="page-link"
                                                previousClassName="page-item"
                                                previousLinkClassName="page-link"
                                                nextClassName="page-item"
                                                nextLinkClassName="page-link"
                                                breakLabel="..."
                                                breakClassName="page-item"
                                                breakLinkClassName="page-link"
                                                pageCount={catalog.totalPages}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={(selectedItem) => setPage(selectedItem.selected)}
                                                containerClassName="pagination"
                                                activeClassName="active"
                                                forcePage={catalog.pageable.pageNumber}
                                            />
                                        </DivPagination>
                                    )}
                                </>
                            )}
                        </section>
                    );
                }}
            </RemoteDataUI.Success>
        </RemoteDataUI>
    );
};

const COMBINABLE_CHARACTERISTICS = { hasCombinable: true };
const LOT_PROMOTION_CHARACTERISTICS = { hasActiveLotPromotion: true };
const DEALS_CHARACTERISTICS = { hasDeals: true };

const SECTIONS = [
    { key: 'combinables', tabLabel: 'Combinables', title: 'OFERTAS COMBINABLES', characteristics: COMBINABLE_CHARACTERISTICS },
    { key: 'caducidad', tabLabel: 'Corta caducidad', title: 'OFERTAS CORTA CADUCIDAD', characteristics: LOT_PROMOTION_CHARACTERISTICS },
    { key: 'ofertas', tabLabel: 'Ofertas', title: 'OFERTAS', characteristics: DEALS_CHARACTERISTICS },
];

export const PromocionesPage = () => {
    const { filterOptionsRemoteData } = useFilterContext();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('todas');
    // Filtros del panel lateral, LOCALES a esta pagina: no tocan el FilterContext global
    // para no arrastrar filtros al catalogo (ni traerlos de alli).
    const [panelFilter, setPanelFilter] = useState({});
    const [viewMode, setViewMode] = useState('grid');

    useDebouncedEffect(() => {
        setSearch(searchInput.trim());
    }, [searchInput], 300);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setSearch(searchInput.trim());
        }
    };

    return (
        <div className="container">
            <Hero>
                <h1>Ofertas</h1>
                <p>Todas las promociones en un solo sitio: combinables, corta caducidad y ofertas.</p>

                <div className="d-flex justify-content-center">
                    <SearchWrapper>
                        <SearchContainer>
                            <SearchInput
                                placeholder="Busca dentro de las ofertas"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                type="text"
                            />
                            <SearchIconWrapper onClick={() => setSearch(searchInput.trim())}>
                                <SearchIcon />
                            </SearchIconWrapper>
                        </SearchContainer>
                    </SearchWrapper>
                </div>
            </Hero>

            <TabsBar>
                <TabButton
                    $active={activeTab === 'todas'}
                    onClick={() => setActiveTab('todas')}
                >
                    Todas
                </TabButton>
                {SECTIONS.map(section => (
                    <TabButton
                        key={section.key}
                        $active={activeTab === section.key}
                        onClick={() => setActiveTab(section.key)}
                    >
                        {section.tabLabel}
                    </TabButton>
                ))}
            </TabsBar>

            <div className="row">
                <aside className="col-12 col-lg-3 mb-4">
                    <RemoteDataUI remoteData={filterOptionsRemoteData}>
                        <RemoteDataUI.Pending>
                            <div className="text-center"><Spinner /></div>
                        </RemoteDataUI.Pending>

                        <RemoteDataUI.Failure>
                            <div className="alert alert-danger">
                                Imposible cargar Filtros. Intentelo más tarde.
                            </div>
                        </RemoteDataUI.Failure>

                        <RemoteDataUI.Success>
                            {filterOptions =>
                                <FilterSection
                                    filterOptions={filterOptions}
                                    filter={panelFilter}
                                    viewMode={viewMode}
                                    onFilterChange={setPanelFilter}
                                    onViewModeChange={setViewMode}
                                />
                            }
                        </RemoteDataUI.Success>
                    </RemoteDataUI>
                </aside>

                <main className="col-12 col-lg-9 p-0">
                    {SECTIONS
                        .filter(section => activeTab === 'todas' || activeTab === section.key)
                        .map(section => (
                            <PromoSection
                                key={section.key}
                                title={section.title}
                                characteristics={section.characteristics}
                                search={search}
                                panelFilter={panelFilter}
                                viewMode={viewMode}
                                hideWhenEmpty={activeTab === 'todas'}
                            />
                        ))}
                </main>
            </div>
        </div>
    );
};
